/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `EventCreationBannerSection` for the Caintra Connect frontend.
 * @module components/EventCreation/EventCreationBannerSection
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { Switch } from "@mui/material";
import { generateAssetUrl } from "../../utils/common.js";
import AssetImage from "../AssetImage";
import {
  isAudienciaNoSociosChecked,
  isAudienciaSociosChecked,
} from "../../utils/eventCreation/eventCreationValidation.js";
import { ReactComponent as UploadIcon } from "../../assets/icons/Cloud_Upload.svg";

const EventCreationBannerSection = ({
  t,
  styles,
  isEditMode,
  formData,
  error,
  imageRef,
  onDragEnter,
  onDragLeave,
  onDrop,
  onImageChange,
  onAudienciaCheckboxChange,
  onActivoChange,
}) => (
  <div className={styles.formGroupOneLeftStack}>
    <div className={styles.imageContainer}>
      <label className={styles.label}>
        {t("pages.eventCreation.eventBanner")}
      </label>
      <div
        ref={imageRef}
        className={styles.dropFileInput}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {isEditMode || formData.imagenBanner ? (
          <AssetImage
            className={styles.uploadedImage}
            src={generateAssetUrl(formData.imagenBanner)}
            alt={t("common.bannerAlt")}
          />
        ) : (
          <div className={styles.uploadContainer}>
            <UploadIcon className={styles.uploadIcon} />
            {error && <span className={styles.errorMessage}>{error}</span>}
            <span className={styles.uploadTitle}>{t("common.drag&drop")}</span>
            <span className={styles.uploadSubtitle}>
              {t("common.yourImage")}{" "}
              <span style={{ color: "#1D5AE8" }} className={styles.uploadSubtitle}>
                {t("common.browseFile")}
              </span>
            </span>
          </div>
        )}
        <input
          className={styles.imageInput}
          type="file"
          accept="image/*"
          onChange={onImageChange}
        />
      </div>
    </div>
    <div className={styles.audienciaBelowBanner}>
      <label className={styles.label}>{t("pages.eventCreation.audience")}</label>
      <div className={styles.audienciaCheckboxRow}>
        <label className={styles.audienciaCheckboxLabel}>
          <input
            type="checkbox"
            checked={isAudienciaSociosChecked(formData.audiencia)}
            onChange={() => onAudienciaCheckboxChange("socios")}
          />
          <span>{t("pages.eventCreation.audienceSocios")}</span>
        </label>
        <label className={styles.audienciaCheckboxLabel}>
          <input
            type="checkbox"
            checked={isAudienciaNoSociosChecked(formData.audiencia)}
            onChange={() => onAudienciaCheckboxChange("noSocios")}
          />
          <span>{t("pages.eventCreation.audienceNonMembers")}</span>
        </label>
      </div>
    </div>
    <div className={styles.activoBelowBanner}>
      <label className={styles.label}>
        {t("pages.eventCreation.activateEvent")}
      </label>
      <Switch
        checked={Boolean(formData.activo)}
        onChange={(event) => onActivoChange(event.target.checked)}
      />
    </div>
  </div>
);

export default EventCreationBannerSection;
